import { useEffect, useState } from "react";
import { Avatar, Grid, Layout, Menu, MenuProps } from "antd";
import { CalendarOutlined, SettingOutlined, ShopOutlined, TableOutlined, UserSwitchOutlined } from "@ant-design/icons";
import { NavLink, useLocation } from "react-router-dom";
import styled from "styled-components";
import { routes } from "common/routes";
import { useAppSelector } from "hooks/useAppSelector";
import { useAppDispatch } from "hooks/useAppDispatch";
import { toggleSider } from "store/app/appSlice";
import { variables } from "common/variables";
import { BsDot } from "react-icons/bs";
import { BiTransfer } from "react-icons/bi";
import { HiUserGroup } from "react-icons/hi";

const { Sider } = Layout;

const StyledSider = styled(Sider)`
  background-color: white;
  border-right: 1px solid #ebebeb;
  
  & .ant-menu {
    overflow-x: hidden;
    overflow-y: auto;
  }
  
`;

const StyledMenu = styled(Menu)`
  height: 100%;
  margin-top: 1rem;
  padding-bottom: 6rem;

  & .ant-menu-submenu-title,
  & .ant-menu-item {
    padding: 0 20px !important;
    font-weight: 500;
    overflow: none;
  }

  & .ant-menu-sub .ant-menu-item {
    padding: 20px 0 20px 2rem !important;
  }

  & .menu-item--wrap .ant-menu-sub .ant-menu-item {
    height: auto;
    min-height: 40px;
    line-height: 1.35;

    & .ant-menu-title-content {
      white-space: normal;
      overflow: visible;
      text-overflow: clip;
    }
  }

  & .menu-item--wrap > .ant-menu-submenu-title {
    height: auto;
    min-height: 40px;
    padding-top: 8px !important;
    padding-bottom: 8px !important;
    line-height: 1.35;

    & .ant-menu-title-content {
      white-space: normal;
      overflow: visible;
      text-overflow: clip;
    }
  }

  & .anticon {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`;

const StyledSubMenuIcon = styled(BsDot)`
  font-size: 20px !important;
`;

type MenuItem = Required<MenuProps>["items"][number];

function getItem(
  label: React.ReactNode,
  key: React.Key,
  icon?: React.ReactNode,
  children?: MenuItem[],
  type?: "group",
  className?: string
): MenuItem {
  return {
    key,
    icon,
    children,
    label,
    type,
    className,
  } as MenuItem;
}

// do not change the order
const rootSubmenuKeys = [
  "store-requisition",
  "store-na-picking",
  "store-rejection",
  "vendors",
  "po",
  "fif",
  "ppd",
  "cluster-society",
  "store-return",
  "admin-services",
  "franchise",
  "procurement",
  "stock-exchange",
  "internal-stock-exchange",
  "user-services",
  "quality-check",
  "middle-mile",
  "cash-collection",
  "cluster-service",
  "franchise-inventory",
  "consumer-service",
  "partner-services",
  "wh-partners",
  "attendance-roster",
  "pidge",
  "delivery-services",
  "platform-configs",
  "user-table-rack-mapping",
];

// Derive the open-submenu key and the set of top-level (accordion) keys
// directly from the rendered menu tree so they can never drift out of sync
// with the sidebar structure.
const getRootKeys = (menuItems: MenuItem[]): string[] =>
  menuItems
    .map((item) => (item as { key?: React.Key } | null)?.key)
    .filter((key): key is string => typeof key === "string");

const findSectionKeyForPath = (
  menuItems: MenuItem[],
  path: string
): string | undefined => {
  for (const item of menuItems) {
    const section = item as { key?: React.Key; children?: MenuItem[] } | null;
    const isMatch = section?.children?.some(
      (child) => (child as { key?: React.Key } | null)?.key === path
    );
    if (isMatch) return typeof section?.key === "string" ? section.key : undefined;
  }
  return undefined;
};

const { useBreakpoint } = Grid;

export const AppSider = () => {
  const { siderCollapsed } = useAppSelector((state) => state.app);
  const dispatch = useAppDispatch();
  const location = useLocation();
  const screens = useBreakpoint();
  const [defaultSelectedKeys, setDefaultSelectedKeys] = useState([
    location?.pathname || "/",
  ]);
  const [openKeys, setOpenKeys] = useState<string[]>([]);

  const onNavClicked = () => {
    if (screens.md !== undefined && !screens.md) {
      setOpenKeys([]);
      setDefaultSelectedKeys([]);
      dispatch(toggleSider());
    }
  };

  const items: MenuItem[] = [
    getItem(
      "Store Requisition",
      rootSubmenuKeys[0],
      <Avatar
        icon={
          <ShopOutlined
            style={{
              color: variables?.primaryColor,
              lineHeight: 0,
            }}
          />
        }
        style={{ backgroundColor: variables?.secondaryColor }}
      />,
      [
        getItem(
          <NavLink to={routes.STORE_REQUISITION.ALL} onClick={onNavClicked}>
            All Requisitions
          </NavLink>,
          `/${routes.STORE_REQUISITION.ALL}`,
          <StyledSubMenuIcon />
        ),
        // getItem(
        //   <NavLink to={routes.STORE_REQUISITION.ALL_SPLIT_SKU} onClick={onNavClicked}>
        //     All Split SKU
        //   </NavLink>,
        //   `/${routes.STORE_REQUISITION.ALL_SPLIT_SKU}`,
        //   <StyledSubMenuIcon />
        // ),
        getItem(
          <NavLink to={routes.STORE_REQUISITION.UPLOAD} onClick={onNavClicked}>
            Upload Store Requisition
          </NavLink>,
          `/${routes.STORE_REQUISITION.UPLOAD}`,
          <StyledSubMenuIcon />
        ),
        getItem(
          <NavLink
            to={routes.STORE_REQUISITION.ALL_DISPATCHED}
            onClick={onNavClicked}
          >
            All Non Dispatched
          </NavLink>,
          `/${routes.STORE_REQUISITION.ALL_DISPATCHED}`,
          <StyledSubMenuIcon />
        ),
        getItem(
          <NavLink
            to={routes.STORE_REQUISITION.SUGGESTED_PICKING}
            onClick={onNavClicked}
          >
            Suggested Inv Picking
          </NavLink>,
          `/${routes.STORE_REQUISITION.SUGGESTED_PICKING}`,
          <StyledSubMenuIcon />
        ),
        getItem(
          <NavLink
            to={routes.STORE_REQUISITION.SEC_PICKING_UPLOAD}
            onClick={onNavClicked}
          >
            Store Sec. Picking
          </NavLink>,
          `/${routes.STORE_REQUISITION.SEC_PICKING_UPLOAD}`,
          <StyledSubMenuIcon />
        ),
      ]
    ),

    getItem(
      "SR User Mapping",
      rootSubmenuKeys[1],
      <Avatar
        icon={
          <ShopOutlined
            style={{ color: variables?.primaryColor, lineHeight: 0 }}
          />
        }
        style={{ backgroundColor: variables?.secondaryColor }}
      />,
      [
        getItem(
          <NavLink
            to={routes.STORE_REQUISITION.USER_MAPPING_UPLOAD}
            onClick={onNavClicked}
          >
            Upload SR User Picking
          </NavLink>,
          `/${routes.STORE_REQUISITION.USER_MAPPING_UPLOAD}`,
          <StyledSubMenuIcon />
        ),
        getItem(
          <NavLink
            to={routes.SR_USER_MAPPING.NA_USER_MAPPING_UPLOAD}
            onClick={onNavClicked}
          >
            Upload SR N/A Picking
          </NavLink>,
          `/${routes.SR_USER_MAPPING.NA_USER_MAPPING_UPLOAD}`,
          <StyledSubMenuIcon />
        ),
        getItem(
          <NavLink
            to={routes.SR_USER_MAPPING.FA_USER_MAPPING_UPLOAD}
            onClick={onNavClicked}
          >
            Upload FA User Mapping
          </NavLink>,
          `/${routes.SR_USER_MAPPING.FA_USER_MAPPING_UPLOAD}`,
          <StyledSubMenuIcon />
        ),
      
      ]
    ),

    // getItem(
    //   'Store Rejection',
    //   rootSubmenuKeys[2],
    //   <Avatar
    //     icon={
    //       <ShopOutlined
    //         style={{ color: variables?.primaryColor, lineHeight: 0 }}
    //       />
    //     }
    //     style={{ backgroundColor: variables?.secondaryColor }}
    //   />,
    //   [
    //     getItem(
    //       <NavLink to={routes.STORE_REJECTION.INVENTORY_REJECTION_UPLOAD} onClick={onNavClicked}>
    //         Store Rejections Upload
    //       </NavLink>,
    //       `/${routes.STORE_REJECTION.INVENTORY_REJECTION_UPLOAD}`,
    //       <StyledSubMenuIcon />
    //     ),
    //   ]
    // ),

    // getItem(
    //   "Vendor Management",
    //   rootSubmenuKeys[3],
    //   <Avatar
    //     icon={
    //       <ShopOutlined
    //         style={{ color: variables?.primaryColor, lineHeight: 0 }}
    //       />
    //     }
    //     style={{ backgroundColor: variables?.secondaryColor }}
    //   />,
    //   [
    //     getItem(
    //       <NavLink to={routes.VENDOR.ALL} onClick={onNavClicked}>
    //         Vendors
    //       </NavLink>,
    //       `/${routes.VENDOR.ALL}`,
    //       <StyledSubMenuIcon />
    //     ),
    //   ]
    // ),

    getItem(
      "Purchase Order",
      rootSubmenuKeys[4],
      <Avatar
        icon={
          <ShopOutlined
            style={{ color: variables?.primaryColor, lineHeight: 0 }}
          />
        }
        style={{ backgroundColor: variables?.secondaryColor }}
      />,
      [
        getItem(
          <NavLink to={routes.VENDOR.ALL} onClick={onNavClicked}>
            Vendors
          </NavLink>,
          `/${routes.VENDOR.ALL}`,
          <StyledSubMenuIcon />
        ),
        getItem(
          <NavLink to={routes.VENDOR.AMENDMENTS} onClick={onNavClicked}>
            Vendor Requests
          </NavLink>,
          `/${routes.VENDOR.AMENDMENTS}`,
          <StyledSubMenuIcon />
        ),
        getItem(
          <NavLink to={routes.PO.UPLOAD} onClick={onNavClicked}>
            Upload PO
          </NavLink>,
          `/${routes.PO.UPLOAD}`,
          <StyledSubMenuIcon />
        ),
        getItem(
          <NavLink to={routes.PURCHASE_INDENT.UPLOAD} onClick={onNavClicked}>
            Upload Purchase Indent
          </NavLink>,
          `/${routes.PURCHASE_INDENT.UPLOAD}`,
          <StyledSubMenuIcon />
        ),
        getItem(
          <NavLink
            to={routes.PO.UPLOAD_BILLING_RATE}
            onClick={onNavClicked}
          >
            Upload Billing Rate
          </NavLink>,
          `/${routes.PO.UPLOAD_BILLING_RATE}`,
          <StyledSubMenuIcon />
        ),
        getItem(
          <NavLink
            to={routes.PURCHASE_INDENT.UPLOAD_VENDOR_ASSIGNMENT}
            onClick={onNavClicked}
          >
            Upload Vendor Assignment
          </NavLink>,
          `/${routes.PURCHASE_INDENT.UPLOAD_VENDOR_ASSIGNMENT}`,
          <StyledSubMenuIcon />
        ),
        getItem(
          <NavLink to={routes.PURCHASE_INDENT.ALL} onClick={onNavClicked}>
            Purchase Indents
          </NavLink>,
          `/${routes.PURCHASE_INDENT.ALL}`,
          <StyledSubMenuIcon />
        ),
        getItem(
          <NavLink to={routes.PO.PO_REQUESTS} onClick={onNavClicked}>
            All PO
          </NavLink>,
          `/${routes.PO.PO_REQUESTS}`,
          <StyledSubMenuIcon />
        ),
        getItem(
          <NavLink to={routes.PO.GRN} onClick={onNavClicked}>
            GRN
          </NavLink>,
          `/${routes.PO.GRN}`,
          <StyledSubMenuIcon />
        ),
        getItem(
          <NavLink to={routes.PO.COMPLETE_BILLING} onClick={onNavClicked}>
            Complete PO Billing
          </NavLink>,
          `/${routes.PO.COMPLETE_BILLING}`,
          <StyledSubMenuIcon />
        ),
        getItem(
          <NavLink to={routes.PO.PURCHASE_CODES} onClick={onNavClicked}>
            Purchase Codes
          </NavLink>,
          `/${routes.PO.PURCHASE_CODES}`,
          <StyledSubMenuIcon />
        ),
        getItem(
          <NavLink to={routes.PO.PURCHASE_CODE_AMENDMENTS} onClick={onNavClicked}>
            Purchase Code Requests
          </NavLink>,
          `/${routes.PO.PURCHASE_CODE_AMENDMENTS}`,
          <StyledSubMenuIcon />
        ),
        getItem(
          <NavLink to={routes.PO.VENDOR_ITEMS} onClick={onNavClicked}>
            Vendor Items
          </NavLink>,
          `/${routes.PO.VENDOR_ITEMS}`,
          <StyledSubMenuIcon />
        ),
        getItem(
          <NavLink to={routes.PO.UPLOAD_MARGIN_TRACKING} onClick={onNavClicked}>
            Upload Margin Tracking
          </NavLink>,
          `/${routes.PO.UPLOAD_MARGIN_TRACKING}`,
          <StyledSubMenuIcon />
        ),
        getItem(
          <NavLink to={routes.PO.DISPATCH_INVOICE} onClick={onNavClicked}>
            Dispatch Invoice
          </NavLink>,
          `/${routes.PO.DISPATCH_INVOICE}`,
          <StyledSubMenuIcon />
        ),
      ]
    ),

    getItem(
      "FIF",
      rootSubmenuKeys[5],
      <Avatar
        icon={
          <ShopOutlined
            style={{
              color: variables?.primaryColor,
              lineHeight: 0,
            }}
          />
        }
        style={{ backgroundColor: variables?.secondaryColor }}
      />,
      [
        getItem(
          <NavLink to={routes.FIF.UPLOAD_DSR} onClick={onNavClicked}>
            Upload FIF DSR
          </NavLink>,
          `/${routes.FIF.UPLOAD_DSR}`,
          <StyledSubMenuIcon />
        ),
      ]
    ),

    getItem(
      "PPD",
      rootSubmenuKeys[6],
      <Avatar
        icon={
          <ShopOutlined
            style={{
              color: variables?.primaryColor,
              lineHeight: 0,
            }}
          />
        }
        style={{ backgroundColor: variables?.secondaryColor }}
      />,
      [
        getItem(
          <NavLink to={routes.PPD.UPLOAD} onClick={onNavClicked}>
            Picker Mapping Upload
          </NavLink>,
          `/${routes.PPD.UPLOAD}`,
          <StyledSubMenuIcon />
        ),
        getItem(
          <NavLink to={routes.PPD.CRON_REMAP_USERS} onClick={onNavClicked}>
            Cron User Remapping
          </NavLink>,
          `/${routes.PPD.CRON_REMAP_USERS}`,
          <StyledSubMenuIcon />
        ),
        getItem(
          <NavLink to={routes.PPD.ORDERS} onClick={onNavClicked}>
            Orders
          </NavLink>,
          `/${routes.PPD.ORDERS}`,
          <StyledSubMenuIcon />
        ),
        getItem(
          <NavLink to={routes.PPD.DELIVERY_ORDERS} onClick={onNavClicked}>
            Delivery Orders
          </NavLink>,
          `/${routes.PPD.DELIVERY_ORDERS}`,
          <StyledSubMenuIcon />
        ),
        getItem(
          <NavLink to={routes.PPD.VIEW_TABLE} onClick={onNavClicked}>
            View Table
          </NavLink>,
          `/${routes.PPD.VIEW_TABLE}`,
          <StyledSubMenuIcon />
        ),
        getItem(
          <NavLink to={routes.PPD.STORE_CONFIGS} onClick={onNavClicked}>
            Store Configs
          </NavLink>,
          `/${routes.PPD.STORE_CONFIGS}`,
          <StyledSubMenuIcon />
        ),
        getItem(
          <NavLink to={routes.PPD.GENERATE_SEQUENCE} onClick={onNavClicked}>
            Generate PPD Sequence
          </NavLink>,
          `/${routes.PPD.GENERATE_SEQUENCE}`,
          <StyledSubMenuIcon />
        ),
        getItem(
          <NavLink to={routes.PPD.AVAILABLE_ORDERS} onClick={onNavClicked}>
            Order Slots Cap
          </NavLink>,
          `/${routes.PPD.AVAILABLE_ORDERS}`,
          <StyledSubMenuIcon />
        ),
        getItem(
          <NavLink to={routes.PPD.AVAILABLE_ORDERS_CONFIG} onClick={onNavClicked}>
            Order Slots Cap Config
          </NavLink>,
          `/${routes.PPD.AVAILABLE_ORDERS_CONFIG}`,
          <StyledSubMenuIcon />
        ),
        getItem(
          <NavLink to={routes.PPD.PIDGE} onClick={onNavClicked}>
            Pidge
          </NavLink>,
          `/${routes.PPD.PIDGE}`,
          <StyledSubMenuIcon />
        ),
        getItem(
          <NavLink to={routes.PPD.MARK_HOLD_ORDERS} onClick={onNavClicked}>
            Mark Hold Orders
          </NavLink>,
          `/${routes.PPD.MARK_HOLD_ORDERS}`,
          <StyledSubMenuIcon />
        ),
        getItem(
          <NavLink to={routes.PPD.DROP_POINTS} onClick={onNavClicked}>
            Drop Point
          </NavLink>,
          `/${routes.PPD.DROP_POINTS}`,
          <StyledSubMenuIcon />
        ),
        getItem(
          <NavLink to={routes.PPD.ITEM_RETURN} onClick={onNavClicked}>
            Item Return
          </NavLink>,
          `/${routes.PPD.ITEM_RETURN}`,
          <StyledSubMenuIcon />
        ),
      ]
    ),

    getItem(
      "Pidge",
      "pidge",
      <Avatar
        icon={
          <ShopOutlined
            style={{
              color: variables?.primaryColor,
              lineHeight: 0,
            }}
          />
        }
        style={{ backgroundColor: variables?.secondaryColor }}
      />,
      [
        getItem(
          <NavLink to={routes.PIDGE.SLOT_CONFIGS} onClick={onNavClicked}>
            Pidge Slot Configs
          </NavLink>,
          `/${routes.PIDGE.SLOT_CONFIGS}`,
          <StyledSubMenuIcon />
        ),
      ]
    ),

    getItem(
      "Delivery Services",
      "delivery-services",
      <Avatar
        icon={
          <ShopOutlined
            style={{
              color: variables?.primaryColor,
              lineHeight: 0,
            }}
          />
        }
        style={{ backgroundColor: variables?.secondaryColor }}
      />,
      [
        getItem(
          <NavLink to={routes.DELIVERY_SERVICES.LOGIN_QR} onClick={onNavClicked}>
            Delivery Login / Trip End / Checkout QR
          </NavLink>,
          `/${routes.DELIVERY_SERVICES.LOGIN_QR}`,
          <StyledSubMenuIcon />
        ),
      ]
    ),

    getItem(
      "Platform Configs",
      "platform-configs",
      <Avatar
        icon={
          <SettingOutlined
            style={{
              color: variables?.primaryColor,
              lineHeight: 0,
            }}
          />
        }
        style={{ backgroundColor: variables?.secondaryColor }}
      />,
      [
        getItem(
          <NavLink to={routes.PLATFORM_CONFIGS.ALL} onClick={onNavClicked}>
            App Configs
          </NavLink>,
          `/${routes.PLATFORM_CONFIGS.ALL}`,
          <StyledSubMenuIcon />
        ),
      ]
    ),

    getItem(
      "Partner Services",
      "partner-services-root",
      <Avatar
        icon={
          <UserSwitchOutlined
            style={{
              color: variables?.primaryColor,
              lineHeight: 0,
            }}
          />
        }
        style={{ backgroundColor: variables?.secondaryColor }}
      />,
      [
        getItem(
          <NavLink to={routes.PARTNER_SERVICES.USER_CHECKOUT_QR} onClick={onNavClicked}>
            User Checkout QR
          </NavLink>,
          `/${routes.PARTNER_SERVICES.USER_CHECKOUT_QR}`,
          <StyledSubMenuIcon />
        ),
        getItem(
          <NavLink to={routes.PARTNER_SERVICES.ONBOARDING_DETAILS} onClick={onNavClicked}>
            Mysa Account Details
          </NavLink>,
          `/${routes.PARTNER_SERVICES.ONBOARDING_DETAILS}`,
          <StyledSubMenuIcon />
        ),
        getItem(
          <NavLink to={routes.PARTNER_SERVICES.UPLOAD_GIG_PAYOUTS} onClick={onNavClicked}>
            Upload Gig Payouts
          </NavLink>,
          `/${routes.PARTNER_SERVICES.UPLOAD_GIG_PAYOUTS}`,
          <StyledSubMenuIcon />
        ),
        getItem(
          <NavLink to={routes.PARTNER_SERVICES.EARNING_PAYOUTS} onClick={onNavClicked}>
            Earning Payouts
          </NavLink>,
          `/${routes.PARTNER_SERVICES.EARNING_PAYOUTS}`,
          <StyledSubMenuIcon />
        ),
        getItem(
          <NavLink to={routes.PARTNER_SERVICES.PROCESS_EARNINGS} onClick={onNavClicked}>
            Process Earnings
          </NavLink>,
          `/${routes.PARTNER_SERVICES.PROCESS_EARNINGS}`,
          <StyledSubMenuIcon />
        ),
      ]
    ),

    getItem(
      "Warehouse Partners",
      "wh-partners-root",
      <Avatar
        icon={
          <UserSwitchOutlined
            style={{
              color: variables?.primaryColor,
              lineHeight: 0,
            }}
          />
        }
        style={{ backgroundColor: variables?.secondaryColor }}
      />,
      [
        getItem(
          <NavLink to={routes.WH_PARTNER_SERVICES.ONBOARDING_DETAILS} onClick={onNavClicked}>
            Mysa Account Details
          </NavLink>,
          `/${routes.WH_PARTNER_SERVICES.ONBOARDING_DETAILS}`,
          <StyledSubMenuIcon />
        ),
        getItem(
          <NavLink to={routes.WH_PARTNER_SERVICES.UPLOAD_GIG_PAYOUTS} onClick={onNavClicked}>
            Upload Gig Payouts
          </NavLink>,
          `/${routes.WH_PARTNER_SERVICES.UPLOAD_GIG_PAYOUTS}`,
          <StyledSubMenuIcon />
        ),
        getItem(
          <NavLink to={routes.WH_PARTNER_SERVICES.EARNING_PAYOUTS} onClick={onNavClicked}>
            Earning Payouts
          </NavLink>,
          `/${routes.WH_PARTNER_SERVICES.EARNING_PAYOUTS}`,
          <StyledSubMenuIcon />
        ),
        getItem(
          <NavLink to={routes.WH_PARTNER_SERVICES.PROCESS_EARNINGS} onClick={onNavClicked}>
            Process Earnings
          </NavLink>,
          `/${routes.WH_PARTNER_SERVICES.PROCESS_EARNINGS}`,
          <StyledSubMenuIcon />
        ),
      ]
    ),

    getItem(
      "Attendance Roster",
      "attendance-roster",
      <Avatar
        icon={
          <CalendarOutlined
            style={{
              color: variables?.primaryColor,
              lineHeight: 0,
            }}
          />
        }
        style={{ backgroundColor: variables?.secondaryColor }}
      />,
      [
        getItem(
          <NavLink to={routes.ATTENDANCE_ROSTER.UPLOAD} onClick={onNavClicked}>
            Upload
          </NavLink>,
          `/${routes.ATTENDANCE_ROSTER.UPLOAD}`,
          <StyledSubMenuIcon />
        ),
        getItem(
          <NavLink to={routes.ATTENDANCE_ROSTER.VIEW} onClick={onNavClicked}>
            View
          </NavLink>,
          `/${routes.ATTENDANCE_ROSTER.VIEW}`,
          <StyledSubMenuIcon />
        ),
      ]
    ),

    getItem(
      "User Tables and Racks Mappings",
      "user-table-rack-mapping",
      <Avatar
        icon={
          <TableOutlined
            style={{
              color: variables?.primaryColor,
              lineHeight: 0,
            }}
          />
        }
        style={{ backgroundColor: variables?.secondaryColor }}
      />,
      [
        getItem(
          <NavLink to={routes.USER_TABLE_RACK_MAPPING.VIEW} onClick={onNavClicked}>
            Table Users mappings
          </NavLink>,
          `/${routes.USER_TABLE_RACK_MAPPING.VIEW}`,
          <StyledSubMenuIcon />
        ),
        getItem(
          <NavLink to={routes.USER_TABLE_RACK_MAPPING.RACK_USERS} onClick={onNavClicked}>
            Rack Users mappings
          </NavLink>,
          `/${routes.USER_TABLE_RACK_MAPPING.RACK_USERS}`,
          <StyledSubMenuIcon />
        ),
      ],
      undefined,
      "menu-item--wrap"
    ),

    getItem(
      "Cluster-Society",
      rootSubmenuKeys[7],
      <Avatar
        icon={
          <ShopOutlined
            style={{
              color: variables?.primaryColor,
              lineHeight: 0,
            }}
          />
        }
        style={{ backgroundColor: variables?.secondaryColor }}
      />,
      [
        getItem(
          <NavLink to={routes.ADMIN_SERVICES.SOCIETY} onClick={onNavClicked}>
            Society
          </NavLink>,
          `/${routes.ADMIN_SERVICES.SOCIETY}`,
          <StyledSubMenuIcon />
        ),
        getItem(
          <NavLink to={routes.CLUSTER_SOCIETY.SOCIETY_AMENDMENTS} onClick={onNavClicked}>
            Society Requests
          </NavLink>,
          `/${routes.CLUSTER_SOCIETY.SOCIETY_AMENDMENTS}`,
          <StyledSubMenuIcon />
        ),
        getItem(
          <NavLink to={routes.CLUSTER_SOCIETY.MERGE_SOCIETY} onClick={onNavClicked}>
            Merge-Society
          </NavLink>,
          `/${routes.CLUSTER_SOCIETY.MERGE_SOCIETY}`,
          <StyledSubMenuIcon />
        ),
        // getItem(
        //   <NavLink to={routes.CLUSTER_SOCIETY.GET_DELAY_REQUEST} onClick={onNavClicked}>
        //     Delay Reasons
        //   </NavLink>,
        //   `/${routes.CLUSTER_SOCIETY.GET_DELAY_REQUEST}`,
        //   <StyledSubMenuIcon />
        // ), 
      ]
    ),

    getItem(
      "Store Return",
      rootSubmenuKeys[9],
      <Avatar
        icon={
          <ShopOutlined
            style={{
              color: variables?.primaryColor,
              lineHeight: 0,
            }}
          />
        }
        style={{ backgroundColor: variables?.secondaryColor }}
      />,
      [
        getItem(
          <NavLink to={routes.STORE_RETURN.CREATE_SR_REQUESTS} onClick={onNavClicked}>
            Create Store Return
          </NavLink>,
          `/${routes.STORE_RETURN.CREATE_SR_REQUESTS}`,
          <StyledSubMenuIcon />
        ),
        getItem(
          <NavLink to={routes.STORE_RETURN.ALL} onClick={onNavClicked}>
            All Store Return
          </NavLink>,
          `/${routes.STORE_RETURN.ALL}`,
          <StyledSubMenuIcon />
        ),
        // getItem(
        //   <NavLink to={routes.STORE_RETURN.UPLOAD} onClick={onNavClicked}>
        //     Upload Store Return
        //   </NavLink>,
        //   `/${routes.STORE_RETURN.UPLOAD}`,
        //   <StyledSubMenuIcon />
        // ),
      ]
    ),

    getItem(
      "Admin Services",
      rootSubmenuKeys[10],
      <Avatar
        icon={
          <UserSwitchOutlined
            style={{
              color: variables?.primaryColor,
              lineHeight: 0,
            }}
          />
        }
        style={{ backgroundColor: variables?.secondaryColor }}
      />,
      [
        getItem(
          <NavLink to={routes.ADMIN_SERVICES.ADMIN_USERS} onClick={onNavClicked}>
            Admin Users
          </NavLink>,
          `/${routes.ADMIN_SERVICES.ADMIN_USERS}`,
          <StyledSubMenuIcon />
        ),
        getItem(
          <NavLink to={routes.ADMIN_SERVICES.UPLOAD_SOCIETY_SEQUENCE} onClick={onNavClicked}>
            Upload Society Sequence
          </NavLink>,
          `/${routes.ADMIN_SERVICES.UPLOAD_SOCIETY_SEQUENCE}`,
          <StyledSubMenuIcon />
        ),
        getItem(
          <NavLink to={routes.ADMIN_SERVICES.UPLOAD_DYNAMIC_CLUSTERING} onClick={onNavClicked}>
            Upload Dynamic Clustering
          </NavLink>,
          `/${routes.ADMIN_SERVICES.UPLOAD_DYNAMIC_CLUSTERING}`,
          <StyledSubMenuIcon />
        ),
      ]
    ),

    getItem(
      "Franchise Services",
      rootSubmenuKeys[11],
      <Avatar
        icon={
          <UserSwitchOutlined
            style={{
              color: variables?.primaryColor,
              lineHeight: 0,
            }}
          />
        }
        style={{ backgroundColor: variables?.secondaryColor }}
      />,
      [
        getItem(
          <NavLink to={routes.FRANCHISE.UPLOAD_PRICING_OVERRIDE} onClick={onNavClicked}>
            Upload Pricing Override
          </NavLink>,
          `/${routes.FRANCHISE.UPLOAD_PRICING_OVERRIDE}`,
          <StyledSubMenuIcon />
        ),
        getItem(
          <NavLink to={routes.FRANCHISE.UPLOAD_CATEGORY_PRICING} onClick={onNavClicked}>
            Upload Category Pricing
          </NavLink>,
          `/${routes.FRANCHISE.UPLOAD_CATEGORY_PRICING}`,
          <StyledSubMenuIcon />
        ),
        getItem(
          <NavLink to={routes.FRANCHISE.UPLOAD_INVENTORY_ADJUSTMENT} onClick={onNavClicked}>
            Upload Inventory Adj
          </NavLink>,
          `/${routes.FRANCHISE.UPLOAD_INVENTORY_ADJUSTMENT}`,
          <StyledSubMenuIcon />
        ),
        getItem(
          <NavLink to={routes.FRANCHISE.ALL} onClick={onNavClicked}>
            All Franchise Inventory
          </NavLink>,
          `/${routes.FRANCHISE.ALL}`,
          <StyledSubMenuIcon />
        ),
      ]
    ),
    getItem(
      "Procurement Services",
      rootSubmenuKeys[12],
      <Avatar
        icon={
          <UserSwitchOutlined
            style={{
              color: variables?.primaryColor,
              lineHeight: 0,
            }}
          />
        }
        style={{ backgroundColor: variables?.secondaryColor }}
      />,
      [

        getItem(
          <NavLink to={routes.PROCUREMENT.UPLOAD_PROJECTED_SR} onClick={onNavClicked}>
            Upload Projected SR
          </NavLink>,
          `/${routes.PROCUREMENT.UPLOAD_PROJECTED_SR}`,
          <StyledSubMenuIcon />
        ),
        getItem(
          <NavLink to={routes.PROCUREMENT.UPLOAD_VENDOR_WAREHOUSE_MAPPING} onClick={onNavClicked}>
            Upload Vendor Warehouse
          </NavLink>,
          `/${routes.PROCUREMENT.UPLOAD_VENDOR_WAREHOUSE_MAPPING}`,
          <StyledSubMenuIcon />
        ),
      ]
    ),
    getItem(
      "Stock Transfer",
      rootSubmenuKeys[13],
      <Avatar
        icon={
          <BiTransfer
            style={{
              color: variables?.primaryColor,
              lineHeight: 0,
              paddingTop: ".24rem",
            }} />}
        style={{ backgroundColor: variables?.secondaryColor }}
      />, [
      getItem(
        <NavLink to={routes.STOCK_EXCHANGE.UPLOAD} onClick={onNavClicked}>
          Upload Stock Transfer
        </NavLink>,
        `/${routes.STOCK_EXCHANGE.UPLOAD}`,
        <StyledSubMenuIcon />
      ),
      getItem(
        <NavLink to={routes.STOCK_EXCHANGE.SENT} onClick={onNavClicked}>
          All Sent Stock Transfer
        </NavLink>,
        `/${routes.STOCK_EXCHANGE.SENT}`,
        <StyledSubMenuIcon />
      ),
      getItem(
        <NavLink to={routes.STOCK_EXCHANGE.RECEIVED} onClick={onNavClicked}>
          All Rcv Stock Transfer
        </NavLink>,
        `/${routes.STOCK_EXCHANGE.RECEIVED}`,
        <StyledSubMenuIcon />
      ),
      getItem(
        <NavLink to={routes.STOCK_EXCHANGE.UPLOAD_HOLD} onClick={onNavClicked}>
          Upload Hold Transfer
        </NavLink>,
        `/${routes.STOCK_EXCHANGE.UPLOAD_HOLD}`,
        <StyledSubMenuIcon />
      ),
    ]),
    getItem(
      "Internal Stock Transfer",
      rootSubmenuKeys[14],
      <Avatar
        icon={
          <BiTransfer
            style={{
              color: variables?.primaryColor,
              lineHeight: 0,
              paddingTop: ".24rem",
            }} />}
        style={{ backgroundColor: variables?.secondaryColor }}
      />, [
      getItem(
        <NavLink to={routes.INTERNAL_STOCK_EXCHANGE.UPLOAD} onClick={onNavClicked}>
          Upload Internal Stock Transfer
        </NavLink>,
        `/${routes.INTERNAL_STOCK_EXCHANGE.UPLOAD}`,
        <StyledSubMenuIcon />
      ),
    ]),
    getItem(
      "User Services",
      rootSubmenuKeys[15],
      <Avatar
        icon={
          <HiUserGroup
            style={{
              color: variables?.primaryColor,
              lineHeight: 0,
              paddingTop: ".24rem",
            }} />}
        style={{ backgroundColor: variables?.secondaryColor }}
      />, [ 
      getItem(
        <NavLink to={routes.USER_SERVICES.UPLOAD_USER_DAY_MAPPING} onClick={onNavClicked}>
          Upload User Day Mapping
        </NavLink>,
        `/${routes.USER_SERVICES.UPLOAD_USER_DAY_MAPPING}`,
        <StyledSubMenuIcon />
      ),
    ]),
    getItem(
      "Quality Checks",
      rootSubmenuKeys[16],
      <Avatar
        icon={
          <HiUserGroup
            style={{
              color: variables?.primaryColor,
              lineHeight: 0,
              paddingTop: ".24rem",
            }} />}
        style={{ backgroundColor: variables?.secondaryColor }}
      />, [
      getItem(
        <NavLink to={routes.QUALITY_CHECK.ALL} onClick={onNavClicked}>
          All Quality Checks
        </NavLink>,
        `/${routes.QUALITY_CHECK.ALL}`,
        <StyledSubMenuIcon />
      ),
    ]),
    getItem(
      "Middle Mile",
      rootSubmenuKeys[17],
      <Avatar
        icon={
          <HiUserGroup
            style={{
              color: variables?.primaryColor,
              lineHeight: 0,
              paddingTop: ".24rem",
            }} />}
        style={{ backgroundColor: variables?.secondaryColor }}
      />, [
      getItem(
        <NavLink to={routes.MIDDLE_MILE.MIDDLE_MILE_UPLOAD} onClick={onNavClicked}>
          Upload Middle Mile
        </NavLink>,
        `/${routes.MIDDLE_MILE.MIDDLE_MILE_UPLOAD}`,
        <StyledSubMenuIcon />
      ),
      getItem(
        <NavLink to={routes.MIDDLE_MILE.TRACKING_INFO} onClick={onNavClicked}>
          Track Middle Mile 
        </NavLink>,
        `/${routes.MIDDLE_MILE.TRACKING_INFO}`,
        <StyledSubMenuIcon />
      ),
      getItem(
        <NavLink to={routes.MIDDLE_MILE.MERGE_ROUTES} onClick={onNavClicked}>
          Merge Routes 
        </NavLink>,
        `/${routes.MIDDLE_MILE.MERGE_ROUTES}`,
        <StyledSubMenuIcon />
      ),
    ]),
    getItem(
      "Cash Collection",
      rootSubmenuKeys[18],
      <Avatar
        icon={
          <HiUserGroup
            style={{
              color: variables?.primaryColor,
              lineHeight: 0,
              paddingTop: ".24rem",
            }} />}
        style={{ backgroundColor: variables?.secondaryColor }}
      />, [
      getItem(
        <NavLink to={routes.CASH_COLLECTION.ALL} onClick={onNavClicked}>
          View Cash Collections
        </NavLink>,
        `/${routes.CASH_COLLECTION.ALL}`,
        <StyledSubMenuIcon />
      ),
      
    ]),

    getItem(
      "Store Cluster Services",
      rootSubmenuKeys[19],
      <Avatar
        icon={
          <UserSwitchOutlined
            style={{
              color: variables?.primaryColor,
              lineHeight: 0,
            }}
          />
        }
        style={{ backgroundColor: variables?.secondaryColor }}
      />,
      [
        getItem(
          <NavLink to={routes.CLUSTER_SERVICE.UPLOAD_CLUSTER_STORE_MAPPING} onClick={onNavClicked}>
            Upload Cluster Store Mapping
          </NavLink>,
          `/${routes.CLUSTER_SERVICE.UPLOAD_CLUSTER_STORE_MAPPING}`,
          <StyledSubMenuIcon />
        ),
        getItem(
          <NavLink to={routes.CLUSTER_SERVICE.UPLOAD_CLUSTER_AM_MAPPING} onClick={onNavClicked}>
            Upload Cluster FA Mapping
          </NavLink>,
          `/${routes.CLUSTER_SERVICE.UPLOAD_CLUSTER_AM_MAPPING}`,
          <StyledSubMenuIcon />
        ),
      ]
    ),
    
    getItem(
      "Franchise Inventory",
      rootSubmenuKeys[20],
      <Avatar
        icon={
          <UserSwitchOutlined
            style={{
              color: variables?.primaryColor,
              lineHeight: 0,
            }}
          />
        }
        style={{ backgroundColor: variables?.secondaryColor }}
      />,
      [
        getItem(
          <NavLink to={routes.FRANCHISE.UPLOAD} onClick={onNavClicked}>
            Upload Franchise Inventory
          </NavLink>,
          `/${routes.FRANCHISE.UPLOAD}`,
          <StyledSubMenuIcon />
        ),
      ]
    ),
    
    getItem(
      "Consumer Services",
      rootSubmenuKeys[21],
      <Avatar
        icon={
          <UserSwitchOutlined
            style={{
              color: variables?.primaryColor,
              lineHeight: 0,
            }}
          />
        }
        style={{ backgroundColor: variables?.secondaryColor }}
      />,
      [
        getItem(
          <NavLink to={routes.CONSUMER_SERVICE.UPLOAD_CLUSTER_MAPPING} onClick={onNavClicked}>
            Upload Clusters
          </NavLink>,
          `/${routes.CONSUMER_SERVICE.UPLOAD_CLUSTER_MAPPING}`,
          <StyledSubMenuIcon />
        ),
        getItem(
          <NavLink to={routes.CONSUMER_SERVICE.UPLOAD_CLUSTER_SOCIETY_MAPPING} onClick={onNavClicked}>
            Upload Cluster Society Mapping
          </NavLink>,
          `/${routes.CONSUMER_SERVICE.UPLOAD_CLUSTER_SOCIETY_MAPPING}`,
          <StyledSubMenuIcon />
        ),
      ]
    ),

  ];

  useEffect(() => {
    if (siderCollapsed === true) {
      setOpenKeys([]);
      setDefaultSelectedKeys([]);
      return;
    }

    const currentPath = location?.pathname || "/";
    // If on home page, expand the first section but don't select any menu item
    if (currentPath === `/${routes.HOME}`) {
      setOpenKeys(getRootKeys(items).slice(0, 1));
      setDefaultSelectedKeys([]);
      return;
    }

    // Open the section that actually contains the active route, derived from
    // the menu tree itself (keeps every panel consistent, no manual mapping).
    const sectionKey = findSectionKeyForPath(items, currentPath);
    setOpenKeys(sectionKey ? [sectionKey] : []);
    setDefaultSelectedKeys([currentPath]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siderCollapsed, location]);

  const onOpenChange = (keys: string[]) => {
    const latestOpenKey = keys.find((key) => openKeys.indexOf(key) === -1);
    const rootKeys = getRootKeys(items);

    if (latestOpenKey && rootKeys.indexOf(latestOpenKey) === -1) {
      setOpenKeys(keys);
    } else {
      setOpenKeys(latestOpenKey ? [latestOpenKey] : []);
    }
  };

  return (
    <StyledSider
      width={screens.md === undefined || screens.md ? 280 : "100vw"}
      breakpoint="md"
      trigger={null}
      collapsible
      collapsedWidth="0"
      collapsed={siderCollapsed}
      style={{
        ...(!screens.md && {
          height: "100%",
          width: "100vw",
          zIndex: 9,
        }),
      }}
    >
      <StyledMenu
        items={items}
        mode="inline"
        defaultSelectedKeys={defaultSelectedKeys}
        openKeys={openKeys}
        onOpenChange={onOpenChange}
      />
    </StyledSider>
  );
};
